import axios, { isAxiosError, isCancel } from 'axios';
import axiosRetry from 'axios-retry';
import { log } from '@/logger/logger.ts';
import { setLogSignedInUserIdSource } from '@/logger/log-formatter.middleware.ts';
import { AuthUser } from '@/types/auth.types';
import { getAppVersion, isVersionGreater as isMajorVersionChanged } from '@/utils/general';
import { useGlobalStore } from '@/state/global.state';
import {
  INCOMING_HEADER_NAMES,
  OUTGOING_HEADER_NAMES,
  TIER_COOKIE_ISSUED_STATUS,
  TIER_COOKIE_OPT_IN_VALUE,
} from '@/consts/headers';
import { getRecipeIdFromPath } from '@/utils/urls';
import { isFigmaEmbedContext } from '@/utils/auth.utils';
import { getIdTokenWithDeadline, TokenRefreshTimeoutError } from '@/services/auth/token-refresh';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import { detectWAFProvider } from '@/services/waf/detection';
import { hasWAFTurnstileSitekey } from '@/services/waf/cloudflare/consts';
import { wafChallengeManager } from '@/services/waf/WAFChallengeManager';
import { recordWAFTelemetry } from '@/services/waf/telemetry';
import { recoverWAFChallenge, type WAFAttemptOutcome, type WAFRecoveryGates } from '@/services/waf/recoverWAFChallenge';
import {
  WAFRecoveryTransport,
  WAFTelemetryOutcomeExtra,
  WAFTelemetryPhase,
  type WAFProvider,
} from '@/services/waf/types';
import { API_ROUTES } from '@/api/apiRoutes';
import server_url from '../globals';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

/**
 * Suspended between clearing the cookie on logout and the next sign-in, so that subsequent requests
 * stop sending the departing user's cookie. The clear DELETE opts back in explicitly to expire it.
 */
let credentialsSuspended = false;

/**
 * Whether a request to the API origin should carry credentials, so the `__Host-wv_tier` and
 * `cf_clearance` cookies ride along. True unless a logout in progress has suspended credentials;
 * logout helpers override the suspension for their own request so the server can expire the cookie.
 */
export const shouldSendCredentials = (): boolean => !credentialsSuspended;

// cf_clearance only rides credentialed requests, so recovery stays off while a logout has suspended them.
const isWAFRecoveryGateOpen = (): boolean => shouldSendCredentials();

// Recovery additionally needs a configured sitekey. Without one no solve can run, so an un-provisioned
// deploy leaves recovery off and passes the 403 through rather than failing every challenged request.
const isWAFRecoveryEnabled = (): boolean => isWAFRecoveryGateOpen() && hasWAFTurnstileSitekey();

const TIER_COOKIE_PATH = API_ROUTES.security.tier();
const SESSION_PATH = API_ROUTES.security.session();
const SIGNIN_PATH = API_ROUTES.users.signin();
const OIDC_TOKEN_PATH = API_ROUTES.auth.figmaOidcToken();
const TIER_BOOTSTRAP_HINT_KEY = 'wv_tier_bootstrapped_at';
/**
 * KEEP_IN_SYNC with `TIER_COOKIE_TTL_SECONDS` in `weavy/core/src/shared/tier-cookie/`: past the
 * cookie's own lifetime it may be gone, so bootstrap again.
 */
const TIER_BOOTSTRAP_HINT_TTL_MS = 30 * 60 * 1000;
/**
 * How long the first requests of a session wait for the bootstrap. It is a cold, credentialed
 * cross-origin POST (DNS, TLS, a CORS preflight, then the POST), which on a slow link takes several
 * seconds, and a request released without the cookie is the one the edge may challenge. A healthy
 * bootstrap settles well inside a second, so only a slow or stalled one ever waits this long.
 */
export const TIER_BOOTSTRAP_DEADLINE_MS = 10_000;
/** Per-request timeout for the two logout DELETEs; `LOGOUT_DEADLINE_MS` bounds each attempt overall. */
const LOGOUT_REQUEST_TIMEOUT_MS = 10_000;
/**
 * Bounds logout requests. Kept below `TOKEN_REFRESH_DEADLINE_MS` so a stalled token refresh on the
 * logout path hits this deadline first (the refresh's own timeout then only suppresses the duplicate
 * log), yet small enough that a stalled refresh cannot hang logout for long.
 */
export const LOGOUT_DEADLINE_MS = 5_000;
/** Back off after a failure instead of prepending a doomed bootstrap to every request. */
const TIER_BOOTSTRAP_RETRY_DELAY_MS = 60_000;

class SessionRevocationTimeoutError extends Error {
  constructor() {
    super('Session revocation timed out');
    this.name = 'SessionRevocationTimeoutError';
  }
}

class SupersededSessionError extends Error {
  constructor() {
    super('Request abandoned: its session was superseded by a new sign-in');
    this.name = 'SupersededSessionError';
  }
}

const isAmbiguousRevocationFailure = (error: unknown): boolean =>
  error instanceof SessionRevocationTimeoutError || isCancel(error) || (isAxiosError(error) && !error.response);

/** A 401 after token refresh means no usable session remains. */
const isSessionAlreadyUnusable = (error: unknown): boolean => isAxiosError(error) && error.response?.status === 401;

// The two logout DELETEs. They stay credentialed through a suspension and may replay a refreshed token
// from a since-departed session (to finish revoking it); no other request may. Query string stripped to
// match `isLoginFlowEndpoint`, so a logout DELETE never loses the exemption over a trailing `?…`.
const isLogoutRequestConfig = (config: { method?: string; url?: string } | undefined): boolean => {
  if (config?.method !== 'delete') {
    return false;
  }
  const path = config.url?.split('?')[0];
  return path === TIER_COOKIE_PATH || path === SESSION_PATH;
};

let tierBootstrapInFlight: Promise<void> | null = null;
// Bumped on each resume; a bootstrap or a stamped request carrying an older value belongs to a
// superseded sign-in and is ignored by both the credential strip and the hint refresh.
let tierSessionGeneration = 0;
/**
 * Authoritative in-memory record of the last attempt. `localStorage` is only a cross-reload
 * optimisation: it is unavailable in a third-party frame under hardened privacy settings, and
 * without this latch every single request would await its own fresh bootstrap.
 */
let lastBootstrapAttempt: { at: number; cookieIssued: boolean } | null = null;

const isWithin = (timestamp: number, windowMs: number): boolean => {
  const age = Date.now() - timestamp;
  // A backwards clock jump would otherwise pin this true forever. NaN fails the >= 0 check.
  return age >= 0 && age < windowMs;
};

const hasBootstrapped = (): boolean => {
  if (lastBootstrapAttempt) {
    const windowMs = lastBootstrapAttempt.cookieIssued ? TIER_BOOTSTRAP_HINT_TTL_MS : TIER_BOOTSTRAP_RETRY_DELAY_MS;
    return isWithin(lastBootstrapAttempt.at, windowMs);
  }

  try {
    const bootstrappedAt = localStorage.getItem(TIER_BOOTSTRAP_HINT_KEY);
    return bootstrappedAt !== null && isWithin(Number(bootstrappedAt), TIER_BOOTSTRAP_HINT_TTL_MS);
  } catch {
    return false;
  }
};

const recordBootstrapAttempt = (cookieIssued: boolean, observedGeneration: number | undefined): void => {
  // Never arm the latch for a departed session: the hint would claim a cookie the next visitor does
  // not have.
  if (credentialsSuspended || observedGeneration !== tierSessionGeneration) {
    return;
  }

  lastBootstrapAttempt = { at: Date.now(), cookieIssued };
  try {
    if (cookieIssued) {
      localStorage.setItem(TIER_BOOTSTRAP_HINT_KEY, String(lastBootstrapAttempt.at));
    }
  } catch {
    // The in-memory latch above already prevents repeat attempts until this page reloads.
  }
};

const forgetBootstrap = (): void => {
  lastBootstrapAttempt = null;
  try {
    localStorage.removeItem(TIER_BOOTSTRAP_HINT_KEY);
  } catch {
    // Nothing was stored, so nothing to forget.
  }
};

// Resets the in-memory tier-bootstrap state that outlives a single test — the `lastBootstrapAttempt`
// latch, the in-flight-dedupe pointer, and the session generation — for a suite that does not reset the
// whole module with `vi.resetModules()`. Bumps the generation before nulling the in-flight pointer (as
// `resumeTierCookie` does) so an orphan bootstrap settling in a later test fails the generation guard
// rather than re-arming the latch or nulling the newer test's pointer.
export const TestOnlyResetTierBootstrapState = (): void => {
  tierSessionGeneration += 1;
  tierBootstrapInFlight = null;
  forgetBootstrap();
};

// A throw at module scope would take down every importer of the HTTP client, so fail closed.
const apiOrigin = ((): string | null => {
  try {
    return new URL(server_url).origin;
  } catch {
    return null;
  }
})();

/**
 * Only our own API may receive credentials. The shared instance is also used with absolute
 * third-party URLs (presigned S3 uploads, asset downloads, source maps); S3 cannot emit
 * `Access-Control-Allow-Credentials` at all, so crediting those requests would block them.
 */
export const isRequestToApiOrigin = (url: string | undefined, baseUrl: string | undefined): boolean => {
  if (!apiOrigin) {
    return false;
  }

  try {
    return new URL(url ?? '', baseUrl || apiOrigin).origin === apiOrigin;
  } catch {
    return false;
  }
};

interface AuthApi {
  getCurrentUser: () => AuthUser | null;
}

// Extend the config interface to include the _retry property
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _tierCookieRetry?: boolean;
  _tierSessionGeneration?: number;
  // Set on the interceptor's own WAF replay so it opts out of re-detection and the request-side clearance
  // wait (the shared loop owns re-challenge handling) while STAYING eligible for opaque-network telemetry,
  // which the caller-facing skipWAFHandling opt-out suppresses.
  _wafReplay?: boolean;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    // Public because callers set it on request options (e.g. the tier-cookie bootstrap POST). The
    // interceptor's own retry/session bookkeeping stays private on ExtendedAxiosRequestConfig above, so
    // a caller cannot forge it.
    skipWAFHandling?: boolean;
    // Skip the pre-request tier-cookie bootstrap prefetch. Set on the session-establishing signin POST,
    // which must not prefetch a tier cookie before the user's session exists. Orthogonal to
    // skipWAFHandling: this never disables WAF challenge recovery.
    skipTierBootstrap?: boolean;
  }
}

/**
 * The marker header is read as well as the body because axios leaves a `blob` or `arraybuffer`
 * response unparsed, so the video download and mask-zip callers would never see the error code.
 */
export const isTierCookieMismatch = (error: AxiosError): boolean => {
  const response = error.response;
  if (response?.status !== 403 || !isRequestToApiOrigin(error.config?.url, error.config?.baseURL)) {
    return false;
  }

  return (
    response.headers?.[INCOMING_HEADER_NAMES.TIER_COOKIE_MISMATCH] !== undefined ||
    (response.data as { internalErrorCode?: number } | undefined)?.internalErrorCode ===
      SERVER_ERROR_CODES.TIER_COOKIE_MISMATCH
  );
};

// Origin scope is the security boundary: the shared instance also serves third-party URLs (S3, CDN,
// source maps) whose responses must never drive a clearance flow.
export const detectApiOriginChallenge = (response?: AxiosResponse): WAFProvider | null => {
  if (!response || !isRequestToApiOrigin(response.config.url, response.config.baseURL)) {
    return null;
  }
  return detectWAFProvider({
    status: response.status,
    headers: response.headers as Record<string, string | undefined>,
  });
};

// The interceptor's first-detection entry point: skip requests the caller opted out of (skipWAFHandling)
// and the interceptor's own replay (_wafReplay). The replay's re-challenge check uses the raw
// detectApiOriginChallenge, since it must detect a challenge on the replay itself.
const detectRecoverableApiOriginChallenge = (response?: AxiosResponse): WAFProvider | null => {
  const config = response?.config as ExtendedAxiosRequestConfig | undefined;
  if (!config || config.skipWAFHandling || config._wafReplay) {
    return null;
  }
  return detectApiOriginChallenge(response);
};

// The two login-flow endpoints (signin and the Figma OIDC token exchange): tags WAF telemetry so their
// recovery is dashboard-separable from product traffic, and exempts them from the 401 refresh-replay.
const isLoginFlowEndpoint = (url: string | undefined): boolean => {
  if (typeof url !== 'string') {
    return false;
  }
  const path = url.split('?')[0];
  return path === SIGNIN_PATH || path === OIDC_TOKEN_PATH;
};

let injectedAuth: AuthApi | null = null;
// axios-retry re-enters this interceptor once per attempt and `warn` ships to Datadog, so a single
// failing credentialed request would emit several remote logs; latch to one shipped warn per session.
let hasWarnedOnCredentialedNetworkError = false;

// The latch is module-scope session state, so a test observing warn-vs-debug must reset it first.
export const TestOnlyResetCredentialedNetworkErrorWarnLatch = () => {
  hasWarnedOnCredentialedNetworkError = false;
};

// Latched once per session, same reason as hasWarnedOnCredentialedNetworkError above.
let hasRecordedOpaqueWAFNetworkError = false;
export const TestOnlyResetOpaqueWAFNetworkErrorLatch = (): void => {
  hasRecordedOpaqueWAFNetworkError = false;
};

// `warn` ships to Datadog, and a benign self-supersede (a request parked while the user's own sign-in
// bumps the generation) is indistinguishable here from a departed-session one, so latch per generation:
// one shipped warn per sign-in, and a benign hit never permanently hides a later real one on the next bump.
let lastWarnedSupersededGeneration = -1;
export const TestOnlyResetSupersededSessionAbandonWarnLatch = (): void => {
  lastWarnedSupersededGeneration = -1;
};
// The recovery gate is open but no sitekey is provisioned, so isWAFRecoveryEnabled() is false purely for
// want of configuration — a misconfigured deploy rather than a disabled feature.
const isWAFRecoveryBlockedOnlyByMissingSitekey = (): boolean => isWAFRecoveryGateOpen() && !hasWAFTurnstileSitekey();

// The shared recovery gate, exported so the ai-chat fetch transport gates identically (one shared gate)
// rather than restating the pair at each call site.
export const wafRecoveryGates: WAFRecoveryGates = {
  isRecoveryEnabled: isWAFRecoveryEnabled,
  isBlockedOnlyByMissingSitekey: isWAFRecoveryBlockedOnlyByMissingSitekey,
};

const axiosInstance = axios.create({
  baseURL: server_url,
});
axiosRetry(axiosInstance, { retries: 3 });

export const getAxiosInstance = () => {
  return axiosInstance;
};

/**
 * Fetch the tier cookie up front. The edge WAF is configured never to challenge this route, so a
 * client that has no cookie yet spends its unprotected request here instead of on a product request
 * that could be challenged. Failures resolve quietly: a missing cookie must never block the app.
 *
 * The deadline is not redundant with the request timeout. Axios applies `timeout` in the adapter,
 * i.e. after interceptors, and this request re-enters the interceptor that awaits a Firebase token
 * refresh. If that never settles, an un-raced shared promise would strand every later request on it.
 */
const bootstrapTierCookie = (): Promise<void> => {
  if (!tierBootstrapInFlight) {
    const generation = tierSessionGeneration;
    let hasSettled = false;
    const settle = (cookieIssued: boolean) => {
      // The POST resolve and the deadline both call this, and a resume may have superseded this
      // bootstrap; either way only the first settle of the still-current session may act, so a stale
      // late resolve cannot null a pointer a newer bootstrap now owns.
      if (hasSettled || generation !== tierSessionGeneration) {
        return;
      }
      hasSettled = true;
      recordBootstrapAttempt(cookieIssued, generation);
      tierBootstrapInFlight = null;
    };
    const request = axiosInstance
      .post(TIER_COOKIE_PATH, undefined, {
        timeout: TIER_BOOTSTRAP_DEADLINE_MS,
        'axios-retry': { retries: 0 },
        skipWAFHandling: true,
      })
      // The cookie is HttpOnly, so this echo is the only evidence one was actually issued. A bare 204
      // means the server minted nothing (no HMAC key, an unmintable uid, or a cookie already on the
      // request that is still valid), and latching on that would claim a cookie we do not hold; the
      // shorter retry window then re-asks instead.
      .then((response) => settle(response.headers[INCOMING_HEADER_NAMES.TIER_COOKIE] === TIER_COOKIE_ISSUED_STATUS))
      .catch(() => settle(false));
    const deadline = new Promise<void>((resolve) => {
      setTimeout(() => {
        settle(false);
        resolve();
      }, TIER_BOOTSTRAP_DEADLINE_MS);
    });
    tierBootstrapInFlight = Promise.race([request, deadline]);
  }

  return tierBootstrapInFlight;
};

/**
 * Suspend credentials without a server round-trip: stop subsequent API-origin requests from carrying
 * the departing user's `__Host-wv_tier` / `cf_clearance` cookies (which also parks WAF challenge
 * recovery, gated on `shouldSendCredentials`), and drop the bootstrap hint. The logout helpers
 * ({@link clearTierCookie}, {@link revokeSession}) run it as their synchronous prefix; the tool page's
 * legacy sign-out calls it directly, since that path expires the cookie on a raw `fetch`.
 */
export const suspendTierCredentials = (): void => {
  forgetBootstrap();
  credentialsSuspended = true;
};

/**
 * Drop the tier cookie on logout. It is `HttpOnly`, so only the server can clear it, and leaving it
 * behind would keep granting the previous user's tier at the edge for the rest of its TTL. Credentials
 * are suspended before the clear is awaited (the DELETE can take seconds), so a response settling in
 * that window can no longer look like a live session and re-arm the departing user's bootstrap hint.
 */
export const clearTierCookie = async (): Promise<void> => {
  suspendTierCredentials();

  const logger = log.getLogger('Axios');
  const abortController = new AbortController();
  let clearSettled = false;
  const clear = axiosInstance
    .delete(TIER_COOKIE_PATH, {
      timeout: LOGOUT_REQUEST_TIMEOUT_MS,
      'axios-retry': { retries: 0 },
      signal: abortController.signal,
      // Explicit: the suspension set just above would otherwise strip the credentials the browser
      // needs to accept this request's expiring cross-origin `Set-Cookie`, leaving the cookie alive.
      withCredentials: true,
    })
    .then(() => {
      clearSettled = true;
    })
    .catch((error) => {
      // Logging out must succeed regardless; an abort is the deadline firing (logged below), but a
      // genuine failure is surfaced so a rollout can see whether logouts are actually clearing.
      clearSettled = true;
      // A refresh timeout is the interceptor giving up on a stalled token, not the clear failing, so
      // treat it like the abort below rather than a genuine clear failure.
      if (!isCancel(error) && !(error instanceof TokenRefreshTimeoutError)) {
        const axiosError = error as AxiosError;
        logger.warn('Tier cookie clear failed on logout', {
          code: axiosError.code,
          status: axiosError.response?.status,
        });
      }
    });
  // The DELETE re-enters the interceptor's token refresh, which axios `timeout` does not bound (it
  // applies in the adapter, after interceptors). Race a deadline so a stalled refresh cannot hang
  // logout, and abort on timeout so a late expiring `Set-Cookie` cannot land and delete the next
  // user's cookie (best-effort: it cannot unwind a `Set-Cookie` whose headers already arrived).
  const deadline = new Promise<void>((resolve) => {
    setTimeout(() => {
      const timedOut = !clearSettled;
      if (timedOut) {
        abortController.abort();
      }
      resolve();
      // Last, so a throwing logger cannot skip the abort/resolve above and leave logout hung.
      if (timedOut) {
        logger.warn('Tier cookie clear timed out on logout');
      }
    }, LOGOUT_DEADLINE_MS);
  });
  await Promise.race([clear, deadline]);
};

/** Revoke Firebase before local sign-out, retrying once when the result is ambiguous. */
export const revokeSession = async (): Promise<void> => {
  suspendTierCredentials();

  const revokeOnce = async (): Promise<void> => {
    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        abortController.abort();
        reject(new SessionRevocationTimeoutError());
      }, LOGOUT_DEADLINE_MS);
    });

    try {
      await Promise.race([
        axiosInstance.delete(SESSION_PATH, {
          timeout: LOGOUT_REQUEST_TIMEOUT_MS,
          'axios-retry': { retries: 0 },
          signal: abortController.signal,
          withCredentials: true,
        }),
        deadline,
      ]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  };

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await revokeOnce();
      return;
    } catch (error) {
      if (isSessionAlreadyUnusable(error)) {
        return;
      }

      // Firebase may have revoked the session before the response was lost.
      const shouldRetry = attempt === 0 && isAmbiguousRevocationFailure(error);
      if (!shouldRetry) {
        throw error;
      }
    }
  }
};

/** Re-arm the cookie for a newly signed-in user after a logout helper suspended it. */
export const resumeTierCookie = (): void => {
  credentialsSuspended = false;
  // Supersede any bootstrap still in flight from the previous session, then drop the pointer so this
  // user starts a fresh one rather than adopting the departed session's.
  tierSessionGeneration += 1;
  tierBootstrapInFlight = null;
  forgetBootstrap();
};

/** Restore credentials after failed logout without invalidating in-flight requests. */
export const keepTierCookieForSurvivingSession = (): void => {
  credentialsSuspended = false;
};

/**
 * An opaque token for the current credential session, captured when a request's credentials are framed.
 * Pair with {@link isCredentialSessionCurrent} to detect a logout (or logout→sign-in) that lands later.
 */
export const captureCredentialSession = (): number => tierSessionGeneration;

/**
 * True only if credentials are still sendable AND the session has not been superseded since `captured`.
 * A logout suspends credentials (first conjunct); a logout→sign-in re-arms them but bumps the generation
 * (second conjunct), so this catches both — which `shouldSendCredentials()` alone does not.
 */
export const isCredentialSessionCurrent = (captured: number): boolean =>
  shouldSendCredentials() && captured === tierSessionGeneration;

// Attached at module import (not lazily from a component) so the interceptors exist before the first
// request can fire — a request can dispatch before any component injects the auth handle, and a lazy
// attach would let it bypass credentials / tier-cookie / WAF recovery.
const attachAxiosInterceptors = () => {
  const logger = log.getLogger('Axios');

  // Errors propagate to the 401 handler's own catch, which logs once (skipping the timeout, which the
  // bounded refresh already diagnosed) — so no try/catch here.
  const refreshToken = async (isLogoutRequest: boolean): Promise<string | null> => {
    const currentUser = injectedAuth?.getCurrentUser() ?? null;
    if (!currentUser) {
      return null;
    }
    const credentialSession = captureCredentialSession();
    const token = await getIdTokenWithDeadline(currentUser, true);
    if (!token) {
      return null;
    }
    if (!isCredentialSessionCurrent(credentialSession)) {
      // The session changed during the up-to-10s refresh, so never install this bearer into the shared
      // defaults (later requests would authenticate as the changed-to user). A logout DELETE may still
      // replay to finish revoking — but only when a plain suspension (same generation) changed the session;
      // a new sign-in bumps the generation, making this the NEW user's token, which a stale DELETE must not
      // use to revoke their session, so surface the 401 instead.
      return isLogoutRequest && credentialSession === tierSessionGeneration ? token : null;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    return token;
  };

  // Add a request interceptor
  axiosInstance.interceptors.request.use(
    async (config: ExtendedAxiosRequestConfig) => {
      const isApiOriginRequest = isRequestToApiOrigin(config.url, config.baseURL);
      // First-write-wins (??= not =): a WAF replay or axios-retry re-enters this interceptor with the
      // original config and must keep the generation it first entered under, so the superseded-session
      // strip below can recognize it. Overwriting the stamp would re-mark the replay as current and let
      // it ride the new session's cookies.
      config._tierSessionGeneration ??= tierSessionGeneration;
      config.withCredentials = config.withCredentials ?? (isApiOriginRequest && shouldSendCredentials());
      const isLogoutRequest = isLogoutRequestConfig(config);
      if (credentialsSuspended && isApiOriginRequest && !isLogoutRequest) {
        // Authoritative over an already-set value, so a pre-logout request replayed after logout
        // (axios-retry, or the 401 refresh replaying the bootstrap POST) cannot carry the cookie back
        // and re-mint. Only the logout DELETEs stay credentialed. `delete()` is case-insensitive,
        // unlike a bracket delete, so a mixed-case opt-in header is still removed.
        config.withCredentials = false;
        config.headers.delete(OUTGOING_HEADER_NAMES.TIER_COOKIE);
      } else if (isApiOriginRequest && config.withCredentials) {
        // Confined to the API origin: a non-simple header would force a preflight on the third-party
        // URLs this instance also serves (presigned S3), which do not expect one.
        config.headers[OUTGOING_HEADER_NAMES.TIER_COOKIE] = TIER_COOKIE_OPT_IN_VALUE;
      }
      const currentUser = injectedAuth?.getCurrentUser() ?? null;
      // Only prefetch on a credentialed request to our own API — never on a third-party URL a caller
      // happened to force credentials onto — and never on the bootstrap route itself.
      const shouldBootstrapTierCookie =
        isApiOriginRequest &&
        !!config.withCredentials &&
        config.url !== TIER_COOKIE_PATH &&
        !isLogoutRequest &&
        !config.skipTierBootstrap &&
        !!currentUser &&
        !hasBootstrapped();
      if (shouldBootstrapTierCookie) {
        try {
          await bootstrapTierCookie();
        } catch {
          // Never let the bootstrap reject the request it was meant to protect.
        }
      }

      // Hold API-origin traffic behind an in-flight clearance so it dispatches once cf_clearance is
      // minted rather than drawing its own challenge; a no-op when none is active, and unbounded in time (a slow
      // clearance shows a reload notice). Safe from self-wait only because the reactive clearance mints via a
      // third-party Turnstile fetch (no API-origin request); a provider that ever calls the API origin
      // mid-clearance must mark that request skipWAFHandling to stay out of this wait.
      if (
        isApiOriginRequest &&
        !isLogoutRequest &&
        !config.skipWAFHandling &&
        !config._wafReplay &&
        isWAFRecoveryEnabled()
      ) {
        const activeClearance = wafChallengeManager.waitForActiveClearance();
        if (activeClearance) {
          await activeClearance;
        }
      }
      const isRetry = config.headers['x-from-retry'] === 'true';
      // Same origin rule as the cookie above, and for the same reason: the bearer is our API's
      // credential and nobody else's. Some of the third-party URLs this instance is handed come from
      // stored document data (a source map derived from an error message, a presigned upload URL),
      // so an author could otherwise point one at a host that logs the viewer's token. Deleting
      // rather than skipping also covers the 401 retry, which re-enters here carrying the header it
      // set itself, and the `axios.defaults` bearer merged in before any interceptor runs.
      if (!isApiOriginRequest) {
        config.headers.delete('Authorization');
      } else if (!isRetry && currentUser) {
        // Don't override Authorization header if it's already set by retry interceptor
        const token = await getIdTokenWithDeadline(currentUser);
        config.headers.Authorization = `Bearer ${token}`;
      }
      const recipeId = getRecipeIdFromPath(window.location.pathname);
      if (recipeId && isApiOriginRequest) {
        config.headers[OUTGOING_HEADER_NAMES.APP_RECIPE_ID] = recipeId;
      }

      // The entry strip ran before the interceptor awaited the bootstrap and a token refresh. Re-check
      // at dispatch, gating on the session too: a request that entered before a logout dispatches
      // uncredentialed, while one that entered before a full logout+sign-in (which clears
      // `credentialsSuspended` again but bumps the generation) is from a since-departed session.
      const enteredInASupersededSession = config._tierSessionGeneration !== tierSessionGeneration;
      if (isApiOriginRequest && !isLogoutRequest) {
        if (enteredInASupersededSession) {
          // The bearer set above is the new user's: de-credentialing alone (the `credentialsSuspended`
          // branch below) would still run this as them, and deleting the bearer would 401 into the
          // refresh path, which sets it again. Reject instead.
          if (lastWarnedSupersededGeneration !== tierSessionGeneration) {
            lastWarnedSupersededGeneration = tierSessionGeneration;
            logger.warn('Abandoned a request from a superseded session', { method: config.method });
          }
          throw new SupersededSessionError();
        }
        if (credentialsSuspended) {
          config.withCredentials = false;
          config.headers.delete(OUTGOING_HEADER_NAMES.TIER_COOKIE);
        }
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      // Check for app version updates
      const supportedAppVersion = response.headers[INCOMING_HEADER_NAMES.SUPPORTED_APP_VERSION] as string;
      const currentVersion = getAppVersion();
      if (supportedAppVersion && currentVersion) {
        if (isMajorVersionChanged(supportedAppVersion, currentVersion)) {
          logger.info(`New app version detected: ${supportedAppVersion} > ${currentVersion}. Setting refresh alert...`);
          useGlobalStore.getState().setIsShowRefreshAlert(true);
        }
      }
      // Every minted response carries the issued echo, not just the dedicated bootstrap, so refresh
      // the hint here too — otherwise the client keeps re-bootstrapping once its hint ages out even
      // though ordinary traffic has already re-minted the cookie. Scoped to our own API origin so a
      // third-party response cannot suppress the prefetch with a spurious echo.
      if (
        response.headers[INCOMING_HEADER_NAMES.TIER_COOKIE] === TIER_COOKIE_ISSUED_STATUS &&
        isRequestToApiOrigin(response.config.url, response.config.baseURL)
      ) {
        recordBootstrapAttempt(true, (response.config as ExtendedAxiosRequestConfig)._tierSessionGeneration);
      }
      return response;
    },
    async (error: AxiosError) => {
      // Both are thrown from the request interceptor and carry no config/response, so short-circuit
      // before the fall-through below logs them as a (console-only, debug-level) `Network or CORS error`.
      if (error instanceof SupersededSessionError || error instanceof TokenRefreshTimeoutError) {
        return Promise.reject(error);
      }
      const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

      // First and most specific (403 + exact cf-mitigated:challenge + API origin), so it cannot shadow
      // a 401, a tier-mismatch 403, or an ordinary 403; none of those carry the challenge signature.
      // Gated on withCredentials too: cf_clearance only rides credentialed requests, so replaying one
      // that dispatched uncredentialed (a logout suspended its credentials) would just burn the bound.
      // Also gated on shouldSendCredentials(): a suspended-credentials request (a logout DELETE, which
      // stays credentialed) can never recover, so skip the detection work entirely for it. And gated on
      // the generation: a challenge on a request stamped under a since-departed session would clear only
      // to have its replay rejected at re-dispatch, so surface the 403 now rather than spend the budget.
      if (
        originalRequest &&
        originalRequest.withCredentials &&
        shouldSendCredentials() &&
        originalRequest._tierSessionGeneration === tierSessionGeneration
      ) {
        const challengeProvider = detectRecoverableApiOriginChallenge(error.response);
        if (challengeProvider !== null) {
          // Stamp at first detection so the latency vital spans the whole recovery, not just the replay.
          const detectedAtMs = performance.now();
          const recovery = await recoverWAFChallenge<AxiosResponse>({
            provider: challengeProvider,
            transport: WAFRecoveryTransport.Axios,
            isLoginFlowRequest: isLoginFlowEndpoint(originalRequest.url),
            detectedAtMs,
            gates: wafRecoveryGates,
            // The replay carries _wafReplay (see its field declaration); a re-challenge it draws (which axios
            // rejects) is re-detected below via the raw detectApiOriginChallenge.
            attempt: async (): Promise<WAFAttemptOutcome<AxiosResponse>> => {
              try {
                const response = await axiosInstance({
                  ...originalRequest,
                  _wafReplay: true,
                } as ExtendedAxiosRequestConfig);
                return { kind: 'succeeded', value: response };
              } catch (retryError) {
                const retryResponse = isAxiosError(retryError) ? retryError.response : undefined;
                if (retryResponse && detectApiOriginChallenge(retryResponse) !== null) {
                  return { kind: 'rechallenged' };
                }
                // A non-challenge failure (the replay's own 401/500, or a network error): axios has already
                // applied 401/tier-mismatch handling, so surface it — the shared loop records no terminal.
                throw retryError;
              }
            },
          });
          if (recovery.kind === 'replayed') {
            return recovery.value;
          }
        }
      }

      // Login-flow endpoints are exempt from the refresh-replay: each POST carries a single-use credential
      // (the signin Turnstile token / the OIDC OAuth code) that the replay would re-send, and a bearer
      // refresh cannot rescue their 401 anyway.
      if (
        error.response &&
        error.response.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isLoginFlowEndpoint(originalRequest.url)
      ) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshToken(isLogoutRequestConfig(originalRequest));
          // No token to refresh with (no auth injected yet, or a signed-out session): replaying
          // `Bearer undefined` just 401s again and burns the one retry, so surface the 401 instead.
          if (!newToken) {
            return Promise.reject(error);
          }
          logger.warn('401 Unauthorized; refreshed the token and retrying the request.');
          const retryConfig = {
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              'x-from-retry': 'true',
              Authorization: `Bearer ${newToken}`,
            },
          };

          return axiosInstance(retryConfig);
        } catch (tokenRefreshError) {
          // A refresh timeout already emitted its own diagnostic; avoid a duplicate error log for it.
          if (!(tokenRefreshError instanceof TokenRefreshTimeoutError)) {
            logger.error('Token refresh error', tokenRefreshError);
          }
        }
      } else if (isTierCookieMismatch(error) && originalRequest && !originalRequest._tierCookieRetry) {
        // A mismatch 403 usually re-mints a cookie in the same response, but the mint hook can skip it,
        // so latch only on the `issued` echo. Retry once either way; with no re-mint it re-surfaces the 403.
        logger.warn('Tier cookie belonged to another user. Retrying once...');
        originalRequest._tierCookieRetry = true;
        if (error.response?.headers?.[INCOMING_HEADER_NAMES.TIER_COOKIE] === TIER_COOKIE_ISSUED_STATUS) {
          recordBootstrapAttempt(true, originalRequest._tierSessionGeneration);
        }
        return axiosInstance({ ...originalRequest, headers: { ...originalRequest.headers } });
      } else if (!error.response) {
        // Aborts and timeouts are routine here (polling, typeahead) and would drown the signal that
        // a credentialed request is being blocked for want of the matching CORS headers.
        const isAbortedOrTimedOut = isCancel(error) || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
        // A credentialed API-origin network failure covered by the recovery gate (credentials). The
        // sitekey is deliberately not required, so this still fires on a no-sitekey deploy, exactly
        // the state where a hidden challenge most needs surfacing. Caller-facing skipWAFHandling routes
        // (e.g. the never-challenged bootstrap POST) are excluded; the interceptor's own replay carries
        // _wafReplay (not skipWAFHandling), so an opaque death there is still surfaced.
        const isOpaqueWAFCandidate =
          !!originalRequest?.withCredentials &&
          !originalRequest.skipWAFHandling &&
          !isAbortedOrTimedOut &&
          isRequestToApiOrigin(originalRequest.url, originalRequest.baseURL) &&
          isWAFRecoveryGateOpen();
        if (isOpaqueWAFCandidate) {
          // Could be a challenge the browser hid for want of the edge CORS transform, indistinguishable
          // from an outage; observe it once (never the generic warn below, regardless of retry attempt)
          // and do not speculatively solve — a false-positive storm during a real outage would spin
          // widgets pointlessly. No provider is attributed: by construction nothing was detected.
          if (!hasRecordedOpaqueWAFNetworkError) {
            hasRecordedOpaqueWAFNetworkError = true;
            recordWAFTelemetry({
              phase: WAFTelemetryPhase.Detected,
              outcome: WAFTelemetryOutcomeExtra.OpaqueNetwork,
              isLoginFlowRequest: isLoginFlowEndpoint(originalRequest?.url),
            });
          } else {
            // Already observed once this session; keep the local diagnostic (and error.code) for the rest.
            logger.debug('Network or CORS error', { code: error.code });
          }
        } else if (originalRequest?.withCredentials && !isAbortedOrTimedOut && !hasWarnedOnCredentialedNetworkError) {
          hasWarnedOnCredentialedNetworkError = true;
          logger.warn('Network or CORS error on a credentialed request', { code: error.code });
        } else {
          logger.debug('Network or CORS error', { code: error.code });
        }
      }

      return Promise.reject(error);
    },
  );
};

attachAxiosInterceptors();

// An embed user's auth uid (`figma:<sub>`) is not their Weave user id (`figma-<sub>`).
setLogSignedInUserIdSource(() => (isFigmaEmbedContext() ? undefined : injectedAuth?.getCurrentUser()?.uid));

/**
 * Supply the active auth handle (`authApi`) to the already-attached interceptors. The interceptors are
 * attached at module load (via {@link attachAxiosInterceptors}); this only injects the handle they read
 * at request time. First injection wins.
 */
export const initAxiosInterceptors = (auth: AuthApi) => {
  injectedAuth ??= auth;
};

export const TestOnlyResetInjectedAuth = (): void => {
  injectedAuth = null;
};
